/**
 * Route registry.
 *
 * PUBLIC_ROUTES is the single source of truth for prerendering, the sitemap and the
 * navigation. A room route can never appear in it: private URLs are generated at
 * runtime and are excluded from the sitemap and from robots by construction.
 */

export interface PublicRoute {
  readonly path: string;
  /** Sitemap priority; the homepage carries the tool itself. */
  readonly priority: number;
  readonly changefreq: 'weekly' | 'monthly';
  /** Whether ad inventory is permitted on this page (substantive content only). */
  readonly adsAllowed: boolean;
}

export const PUBLIC_ROUTES = [
  /*
   * The homepage carries the tool, so its ad slot sits at the very bottom — after every
   * explanatory section, far from the creation card. Specification §8.1 lists display-ad
   * inventory among the below-fold homepage content; the constraint it sets is that an ad
   * must never resemble Create, Submit, Share, the result or navigation.
   */
  { path: '/', priority: 1.0, changefreq: 'weekly', adsAllowed: true },
  { path: '/how-it-works', priority: 0.8, changefreq: 'monthly', adsAllowed: true },
  { path: '/privacy', priority: 0.6, changefreq: 'monthly', adsAllowed: false },
  { path: '/faq', priority: 0.7, changefreq: 'monthly', adsAllowed: true },
  /*
   * The guides index. `adsAllowed: false` on purpose: it is a short page of navigation
   * plus a few paragraphs, and Google's publisher policy forbids ads on screens with
   * little publisher content. An index page is signposting, not an article.
   */
  { path: '/guides', priority: 0.7, changefreq: 'monthly', adsAllowed: false },
  /* The article index. Same reasoning as /guides: signposting, not an article. */
  { path: '/blog', priority: 0.7, changefreq: 'weekly', adsAllowed: false },
  { path: '/group-trip-budget', priority: 0.9, changefreq: 'monthly', adsAllowed: true },
  { path: '/bachelorette-budget', priority: 0.9, changefreq: 'monthly', adsAllowed: true },
  { path: '/group-gift-budget', priority: 0.9, changefreq: 'monthly', adsAllowed: true },
] as const satisfies readonly PublicRoute[];

export type PublicPath = (typeof PUBLIC_ROUTES)[number]['path'];

/** Rendered by the Worker for anything unmatched. Never in the sitemap. */
export const NOT_FOUND_PATH = '/404';

export function isPublicPath(path: string): path is PublicPath {
  const normalised = normalisePath(path);
  return PUBLIC_ROUTES.some((r) => r.path === normalised);
}

export function getPublicRoute(path: string): PublicRoute | undefined {
  const normalised = normalisePath(path);
  return PUBLIC_ROUTES.find((r) => r.path === normalised);
}

/**
 * Whether Google's ad loader may be emitted on a page.
 *
 * Articles are not in the registry above, because they are added as content rather than
 * as routes, and they do carry ads: an article is substantive publisher content, which is
 * exactly the case the policy permits. Everything else defers to its `adsAllowed` flag,
 * and anything unknown gets no ads.
 */
export function adsAllowedForPath(path: string): boolean {
  const normalised = normalisePath(path);
  if (/^\/blog\/[a-z0-9-]+$/.test(normalised)) return true;
  return getPublicRoute(normalised)?.adsAllowed === true;
}

/** `/faq/` and `/faq` are the same page; `/` stays `/`. */
export function normalisePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path === '' ? '/' : path;
}
